import type { CaptureResult, Properties } from 'posthog-js'

/** Default PostHog properties that store full page URLs. */
const URL_PROPERTIES = [
  '$current_url',
  '$initial_current_url',
  '$session_entry_url',
  '$referrer',
  '$initial_referrer',
] as const

function isAshPortalHost(host: string): boolean {
  return host === 'localhost:3000' || host.endsWith('poweredbyash.io')
}

/**
 * Removes the Stytch `token` query param from Ash portal URLs.
 * Leaves other hosts and unparseable strings unchanged.
 */
export function redactSensitiveUrlParams(urlString: string): string {
  try {
    const url = new URL(urlString)
    if (!isAshPortalHost(url.host) || !url.searchParams.has('token')) {
      return urlString
    }
    url.searchParams.delete('token')
    return url.toString()
  } catch {
    return urlString
  }
}

function redactUrlProperties(properties: Properties): void {
  for (const key of URL_PROPERTIES) {
    const value = properties[key]
    if (typeof value === 'string') {
      properties[key] = redactSensitiveUrlParams(value)
    }
  }
}

/** PostHog `before_send` hook: strip Stytch tokens from Ash portal URLs in default URL properties. */
export function sanitizePosthogEventUrls(event: CaptureResult | null): CaptureResult | null {
  if (!event) {
    return null
  }

  if (event.properties) {
    redactUrlProperties(event.properties)
  }
  if (event.$set) {
    redactUrlProperties(event.$set)
  }
  if (event.$set_once) {
    redactUrlProperties(event.$set_once)
  }

  return event
}
